import React, { useState } from 'react'
import './Textarea.css'

/**
 * Reusable Textarea Component
 *
 * A customizable multiline text input with label, character count, and error handling
 *
 * @param {Object} props - Component props
 * @param {string} props.label - Textarea label
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.value - Textarea value
 * @param {Function} props.onChange - Change handler
 * @param {string} props.error - Error message
 * @param {string} props.helpText - Helper text below textarea
 * @param {boolean} props.required - Required field
 * @param {boolean} props.disabled - Disabled state
 * @param {number} props.rows - Number of visible rows (default: 4)
 * @param {number} props.maxLength - Maximum character length
 * @param {boolean} props.showCount - Show character count (default: false)
 * @param {boolean} props.resize - Allow resize (default: 'vertical')
 * @param {string} props.className - Additional CSS classes
 * @param {React.Ref} ref - Forwarded ref for React Hook Form compatibility
 */
const Textarea = React.forwardRef(
	(
		{
			label,
			placeholder,
			value,
			onChange,
			error,
			helpText,
			required = false,
			disabled = false,
			rows = 4,
			maxLength,
			showCount = false,
			resize = 'vertical',
			className = '',
			id,
			...props
		},
		ref
	) => {
		const [isFocused, setIsFocused] = useState(false)
		const [charCount, setCharCount] = useState(value?.length || 0)

		const textareaId = id || `textarea-${label?.replace(/\s+/g, '-').toLowerCase()}`
		const baseClass = 'textarea'
		const wrapperClasses = [
			`${baseClass}-wrapper`,
			error && `${baseClass}-wrapper--error`,
			isFocused && `${baseClass}-wrapper--focused`,
			disabled && `${baseClass}-wrapper--disabled`,
			className,
		]
			.filter(Boolean)
			.join(' ')

		const handleChange = (e) => {
			setCharCount(e.target.value.length)
			if (onChange) onChange(e)
		}

		return (
			<div className={wrapperClasses}>
				{label && (
					<label htmlFor={textareaId} className={`${baseClass}__label`}>
						{label}
						{required && <span className={`${baseClass}__required`}>*</span>}
					</label>
				)}

				{helpText && !error && (
					<span className={`${baseClass}__help-text`}>{helpText}</span>
				)}

				<textarea
					id={textareaId}
					className={`${baseClass}__field ${baseClass}__field--resize-${resize}`}
					placeholder={placeholder}
					value={value}
					onChange={handleChange}
					disabled={disabled}
					required={required}
					rows={rows}
					maxLength={maxLength}
					onFocus={() => setIsFocused(true)}
					onBlur={() => setIsFocused(false)}
					ref={ref}
					{...props}
				/>

				<div className={`${baseClass}__footer`}>
					{error && <span className={`${baseClass}__error`}>{error}</span>}
					{showCount && maxLength && (
						<span className={`${baseClass}__count`}>
							{charCount}/{maxLength}
						</span>
					)}
				</div>
			</div>
		)
	}
)

Textarea.displayName = 'Textarea'

export default Textarea
